import { memo, ReactNode } from 'react';

import Conversion from 'bloko/blocks/conversion';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import NumberFormatter from 'bloko/common/numberFormatter';
import { formatToReactComponent } from 'bloko/common/trl';

import translation from 'src/components/translation';
import { TariffProductType } from 'src/models/price/product.types';
import { ServiceItemCode } from 'src/models/price/productCodes';
import { getPublicationSubProduct } from 'src/utils/price/products';

const TrlKeys = {
    publicationsCount: {
        one: 'price.publicationsCountWithName.one',
        some: 'price.publicationsCountWithName.some',
        many: 'price.publicationsCountWithName.many',
    },
    publicationTitle: {
        [ServiceItemCode.ZpPromo]: 'price.publicationTitle.ZP_PROMO',
        [ServiceItemCode.ZpBusiness]: 'price.publicationTitle.ZP_BUSINESS',
        [ServiceItemCode.ZpAnonymous]: 'price.publicationTitle.ZP_ANONYMOUS',
        [ServiceItemCode.ZpSprint]: 'price.publicationTitle.ZP_SPRINT',
    },
};

const RawPublicationsCount: TranslatedComponent<{ code: ServiceItemCode; count: number }> = ({ code, count, trls }) => {
    const trlKeys = TrlKeys.publicationTitle;
    const publicationTitleKey = trlKeys[code as keyof typeof trlKeys];
    const publicationTitle = publicationTitleKey ? trls[publicationTitleKey] : '';
    return count ? (
        // span для простановки атрибута source
        <span>
            <Conversion
                one={trls[TrlKeys.publicationsCount.one]}
                some={trls[TrlKeys.publicationsCount.some]}
                many={trls[TrlKeys.publicationsCount.many]}
                format={(trl) =>
                    formatToReactComponent(trl, {
                        '{0}': <span data-qa="publications-count">{NumberFormatter.format(String(count))}</span>,
                        '{1}': <span data-qa="publications-title">{publicationTitle}</span>,
                    })
                }
                value={count}
                hasValue={false}
            />
        </span>
    ) : null;
};

const PublicationsCount = memo(translation(RawPublicationsCount));

/** 10 вакансий «Бизнес» */
const usePublicationsCount = (product: TariffProductType | null): ReactNode => {
    const publicationProduct = product && getPublicationSubProduct(product);
    const publicationsCount = publicationProduct ? parseInt(publicationProduct.count, 10) : null;
    return publicationProduct?.code && publicationsCount ? (
        <PublicationsCount code={publicationProduct.code} count={publicationsCount} />
    ) : null;
};

export { usePublicationsCount };
